import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type {
  TranslationMemoryCollection,
  TranslationMemoryCollectionInput,
} from '@src/repositories/TranslationMemoryCollectionRepository';
import type { Result } from '@src/services/common/Result';
import type { TranslationMemoryCollectionService } from '@src/services/TranslationMemoryCollectionService';

const logger = LogManager.getLogger('stores:TranslationMemory');

type EntryStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TranslationMemoryCollectionsState {
  status: EntryStatus;
  collections: TranslationMemoryCollection[];
  error: string | null;
}

interface Slice extends TranslationMemoryCollectionsState {
  inFlight: Promise<void> | null;
}

/** The person authoring a write — stamped into the local `createdByUser`. */
export interface AuthorRef {
  id: string;
  name?: string;
}

const emptySlice = (): Slice => ({
  status: 'idle',
  collections: [],
  error: null,
  inFlight: null,
});

/**
 * Workspace translation-memory collections, cached per showcase so the Settings
 * tab loads them once and reuses them across opens (and, later, other TM
 * surfaces). Mirrors `DistributionChannelsStore`: an observable showcase→slice
 * map replaced wholesale, load-once unless forced, and mutations that apply the
 * service's confirmed result in place (the GraphQL list lags CMA writes, so a
 * refetch would re-introduce a just-deleted / omit a just-created collection).
 */
export class TranslationMemoryStore {
  private entries = observable.map<string, Slice>(undefined, { deep: false });

  constructor(private service: TranslationMemoryCollectionService) {
    makeAutoObservable<this, 'service'>(this, { service: false });
  }

  getState(showcaseId: string): TranslationMemoryCollectionsState {
    const slice = this.entries.get(showcaseId) ?? emptySlice();
    return {
      status: slice.status,
      collections: slice.collections,
      error: slice.error,
    };
  }

  loadForShowcase(
    showcaseId: string,
    options: { force?: boolean } = {},
  ): Promise<void> {
    const existing = this.entries.get(showcaseId) ?? emptySlice();
    if (!options.force) {
      if (existing.status === 'ready') return Promise.resolve();
      if (existing.inFlight) return existing.inFlight;
    }

    const promise = this.service.fetchForShowcase(showcaseId).then(result => {
      runInAction(() => {
        const current = this.entries.get(showcaseId) ?? emptySlice();
        if (result.ok) {
          this.entries.set(showcaseId, {
            status: 'ready',
            collections: result.value,
            error: null,
            inFlight: null,
          });
        } else {
          logger.warn(
            'Failed to load translation memory collections',
            result.error,
          );
          this.entries.set(showcaseId, {
            status: 'error',
            collections: current.collections,
            error: "Couldn't load translation memory collections.",
            inFlight: null,
          });
        }
      });
    });

    this.entries.set(showcaseId, {
      ...existing,
      status: 'loading',
      error: null,
      inFlight: promise,
    });

    return promise;
  }

  async createCollection(
    showcaseId: string,
    input: TranslationMemoryCollectionInput,
    author: AuthorRef | null,
  ): Promise<Result<TranslationMemoryCollection>> {
    const result = await this.service.create(
      showcaseId,
      input,
      author?.id ?? null,
    );
    if (!result.ok) return result;
    const created: TranslationMemoryCollection = {
      ...result.value,
      createdByUser: author ? { id: author.id, name: author.name } : null,
    };
    runInAction(() => {
      const current = this.entries.get(showcaseId) ?? emptySlice();
      this.entries.set(showcaseId, {
        ...current,
        status: 'ready',
        collections: sortByName([...current.collections, created]),
        inFlight: null,
      });
    });
    return { ok: true, value: created };
  }

  async updateCollection(
    showcaseId: string,
    id: string,
    input: TranslationMemoryCollectionInput,
    author: AuthorRef | null,
  ): Promise<Result<TranslationMemoryCollection>> {
    const result = await this.service.update(id, input, author?.id ?? null);
    if (!result.ok) return result;
    runInAction(() => {
      const current = this.entries.get(showcaseId) ?? emptySlice();
      this.entries.set(showcaseId, {
        ...current,
        status: 'ready',
        collections: sortByName(
          current.collections.map(c =>
            c.id === id
              ? { ...c, name: input.name, description: input.description ?? '' }
              : c,
          ),
        ),
        inFlight: null,
      });
    });
    return result;
  }

  async deleteCollection(
    showcaseId: string,
    id: string,
  ): Promise<Result<void>> {
    const result = await this.service.delete(id);
    if (!result.ok) return result;
    runInAction(() => {
      const current = this.entries.get(showcaseId) ?? emptySlice();
      this.entries.set(showcaseId, {
        ...current,
        status: 'ready',
        collections: current.collections.filter(c => c.id !== id),
        inFlight: null,
      });
    });
    return result;
  }

  invalidate(showcaseId?: string): void {
    if (showcaseId) this.entries.delete(showcaseId);
    else this.entries.clear();
  }
}

const sortByName = (
  collections: TranslationMemoryCollection[],
): TranslationMemoryCollection[] =>
  [...collections].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
